import { Link } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { PHONE_DISPLAY, PHONE_TEL } from "@/components/shared";

const HERO_IMG = "https://images.unsplash.com/photo-1586024486164-ce9b3d87e09f?q=85&w=1600&auto=format&fit=crop";
const CANONICAL = "https://durotech28.me/dfw-tv-mounting";
const OG_IMAGE = "https://customer-assets-0z36b82j.emergentagent.net/job_e11fd24c-7b48-4e17-adcd-1af3744f40d4/artifacts/6221c013a817eb03_file_000000005b84822fa0d34d662b8734ac.png";

const CITIES = ["Irving", "Dallas", "Fort Worth", "Arlington", "Grand Prairie", "Coppell", "Carrollton",
  "Farmers Branch", "Plano", "Frisco", "McKinney", "Allen", "Richardson", "Lewisville"];

const CITY_NOTES = {
  "Irving": "Our home base in the mid-cities. We mount TVs throughout Irving — from apartments and townhomes near Las Colinas to single-family homes in Valley Ranch — with same-week appointments usually available.",
  "Dallas": "From high-rise condos in Uptown and Downtown to ranch-style homes in Oak Cliff and Lake Highlands, Dallas installs range from simple bedroom mounts to full living-room entertainment setups with in-wall wire concealment.",
  "Fort Worth": "We serve Fort Worth homeowners from Alliance to Benbrook and everywhere in between. Brick and stone exteriors are common here — we carry the right bits and anchors for every wall type.",
  "Arlington": "Between the stadiums and the suburbs, Arlington calls often involve game-day setups: big screens, soundbars and clean cable runs. We mount TVs in homes, apartments and offices across the city.",
  "Grand Prairie": "Grand Prairie homes near Joe Pool Lake and throughout the city get the same careful service — stud-finding, level mounting and tidy wire management on every job, whatever the wall material.",
  "Coppell": "Coppell's family homes often mean living-room feature walls and above-fireplace installs. We help plan viewing height and heat-safe mounting before drilling a single hole.",
  "Carrollton": "In Carrollton we handle everything from bedroom TVs to mounted outdoor setups on covered patios. Older homes with plaster or panel walls are no problem — we assess before we mount.",
  "Farmers Branch": "Farmers Branch is right in our daily service route, so scheduling is quick. Townhomes and newer builds here often have metal studs — we bring the correct anchors for a secure mount.",
  "Plano": "Plano installations frequently pair TV mounting with smart home devices — doorbells, thermostats and locks in the same visit. One appointment, one technician, everything connected.",
  "Frisco": "Frisco's newer construction means open-concept living rooms and large screens — 75-inch and 85-inch mounts are our most common calls here, including above-fireplace installations.",
  "McKinney": "From historic homes near the square to new builds on the north side, McKinney jobs get a wall assessment first, then a mount matched to the TV, the wall and the room.",
  "Allen": "Allen families book us for media-room setups and bedroom mounts alike. Soundbar mounting and wire concealment are popular add-ons for a clean, finished look.",
  "Richardson": "Richardson's established neighborhoods mean a mix of wall types — drywall, paneling and brick. We arrive prepared for all of them, with mounts and hardware on the truck.",
  "Lewisville": "In Lewisville we mount TVs in homes, garages and game rooms across the city. Call for a quick quote or book online — most standard installs are finished in about an hour.",
};

const PRICE_ROWS = [
  ["Up to 50 inches", "$70"], ["51–64 inches", "$125"], ["65–75 inches", "$175"],
  ["76–85 inches", "$250"], ["86–100 inches", "$350"],
];

const EXTRAS = [
  ["Standard flat TV bracket", "$40"],
  ["Tilt bracket", "$50"],
  ["Heavy-duty or specialty brackets", "Quoted separately"],
  ["Surface wire raceway", "$50"],
  ["Standard in-wall wire concealment", "$150"],
  ["Fireplace TV mounting", "$300–$750, depending on complexity"],
];

const SERVICES = [
  ["Residential TV Wall Mounting", "Clean, secure wall mounting for living rooms, bedrooms and game rooms. We locate studs, level the mount and set your TV at the right viewing height — drywall, brick, concrete and stone."],
  ["Large-Screen TV Installation", "Big screens need the right hardware and handling. We mount TVs up to 100 inches with heavy-duty brackets matched to your TV's weight and VESA pattern."],
  ["Fireplace TV Mounting", "Above-the-fireplace installs done safely — heat considerations, viewing angle and the right bracket for brick or stone. Priced by complexity after a quick look at your setup."],
  ["In-Wall & Surface Wire Concealment", "No dangling cables. Choose a discreet surface raceway or full in-wall concealment for a clean, finished look. New outlet work is coordinated with licensed electricians."],
  ["TV Bracket Installation", "Already have a TV on the wall that needs a better bracket? We install customer-supplied flat, tilt and full-motion brackets, or supply qualifying brackets for you."],
  ["Soundbar Mounting", "Your soundbar mounted directly below the TV, aligned and connected, with cables concealed to match the rest of the installation."],
  ["Home Theater & Entertainment Setup", "From streaming devices to full surround setups — we connect, configure and tidy your entire entertainment system, not just the screen."],
  ["TV Mounting with Shelving & Entertainment Centers", "Mounting above consoles, inside built-ins and alongside shelving. We plan clearances and cable paths so everything fits and stays accessible."],
];

const FAQS = [
  ["How much does TV mounting cost?",
   "Installation starts at $70 for TVs up to 50 inches, $125 for 51–64 inches, $175 for 65–75 inches, $250 for 76–85 inches and $350 for 86–100 inches. These are starting rates — specialty brackets, materials, electrical work and unusual mounting conditions may cost extra, and we confirm the final price before work begins."],
  ["Do you provide TV mounting brackets?",
   "Yes. A standard flat bracket is $40 and a tilt bracket is $50. Heavy-duty or specialty brackets are quoted separately, and our 76-inch and larger packages include a heavy-duty flat mount. You're also welcome to supply your own compatible bracket."],
  ["Can you mount a TV above a fireplace?",
   "Yes — fireplace TV mounting is one of our specialties, typically $300–$750 depending on wall material, heat considerations and cable routing. Book online and we'll confirm the exact price after reviewing your setup photos."],
  ["Can you hide the wires inside the wall?",
   "Yes. A surface wire raceway is $50 and standard in-wall wire concealment is $150 for suitable walls. If your project needs new electrical outlets or complex routing, that work is coordinated with licensed electricians and quoted separately."],
  ["Do I need to purchase the TV before booking?",
   "Yes — have your TV on site for the appointment. If you haven't chosen a bracket yet, no problem: we can supply a qualifying bracket or help you pick the right one."],
  ["Can I pay after installation?",
   "Yes. Pay After Installation is available — you pay when the work is completed. Installation-only bookings require no upfront payment at all. Mount packages may require a small deposit, which is credited toward your total, and secure online payment via PayPal is always optional."],
  ["What cities does DuroTech28 serve?",
   "We serve the Dallas–Fort Worth Metroplex, including Irving, Dallas, Fort Worth, Arlington, Grand Prairie, Coppell, Carrollton, Farmers Branch, Plano, Frisco, McKinney, Allen, Richardson and Lewisville."],
  ["How do I book a TV installation?",
   "Two easy ways: call 469-897-6497 and we'll book you by phone, or use the Book Online button to pick your TV size, mount package and appointment time in about two minutes."],
];

const BUNDLES = [
  {
    price: 275, title: "TV Mounting + Electric Fireplace Installation", testid: "bundle-fireplace",
    items: ["TV installation up to 75 inches", "Electric fireplace installation",
      "Free qualifying standard flat bracket for TVs up to 70 inches",
      "Promotional in-wall wire concealment: +$25, where applicable",
      "Recessed electrical work: +$125, where applicable",
      "Customer supplies the TV and electric fireplace",
      "Specialty materials, obstructions and complex work quoted separately"],
  },
  {
    price: 450, title: "TV Mounting + Smart Home Installation", testid: "bundle-smart-home",
    items: ["TV mounting", "Video doorbell installation", "Smart thermostat installation",
      "Smart lock installation", "Free qualifying standard flat bracket for TVs up to 70 inches",
      "Smart devices supplied by the customer or purchased separately",
      "Specialty brackets and additional work quoted separately"],
  },
];

const btnGold = "inline-block px-8 py-4 rounded bg-gradient-to-r from-[#d4af37] to-[#e6ca65] text-black font-bold uppercase tracking-wider text-sm hover:opacity-95 transition-opacity shadow-lg shadow-[#d4af37]/25";
const btnCall = "inline-block px-8 py-4 rounded border-2 border-[#d4af37] text-[#d4af37] font-bold uppercase tracking-wider text-sm hover:bg-[#d4af37] hover:text-black transition-colors";

function CTAPair({ idPrefix }) {
  return (
    <div className="flex flex-wrap justify-center gap-4">
      <a href={PHONE_TEL} data-testid={`${idPrefix}-call-btn`} className={btnCall}>
        Call Now — Book by Phone
      </a>
      <Link to="/book" data-testid={`${idPrefix}-book-btn`} className={btnGold}>
        Book Online
      </Link>
    </div>
  );
}

const jsonLd = [
  {
    "@context": "https://schema.org", "@type": "Service",
    "name": "Professional TV Mounting",
    "serviceType": "TV wall mounting, fireplace TV installation, wire concealment, home entertainment setup",
    "provider": { "@type": "LocalBusiness", "name": "DuroTech28", "telephone": "+1-469-897-6497", "url": "https://durotech28.me" },
    "areaServed": CITIES.map((c) => ({ "@type": "City", "name": `${c}, TX` })),
    "url": CANONICAL,
  },
  {
    "@context": "https://schema.org", "@type": "FAQPage",
    "mainEntity": FAQS.map(([q, a]) => ({
      "@type": "Question", "name": q,
      "acceptedAnswer": { "@type": "Answer", "text": a },
    })),
  },
];

export default function DFWTVMountingPage() {
  return (
    <>
      <Seo
        title="TV Mounting Dallas–Fort Worth, TX | DuroTech28"
        description="Professional TV mounting in Dallas–Fort Worth, Irving, Plano, Frisco and nearby areas. Fireplace mounting, wire concealment and home theater setup. Call 469-897-6497."
        canonical={CANONICAL}
        ogImage={OG_IMAGE}
        jsonLd={jsonLd}
      />
      <Nav />
      <main data-testid="dfw-tv-mounting-page">
        {/* Hero */}
        <header className="relative overflow-hidden border-b border-[#d4af37]/15">
          <img src={HERO_IMG} alt="Professional TV wall mounting with concealed wires in a Dallas–Fort Worth living room" className="absolute inset-0 w-full h-full object-cover opacity-35" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0c]/70 via-[#0a0a0c]/85 to-[#0a0a0c]"></div>
          <div className="relative max-w-4xl mx-auto px-6 pt-20 pb-16 text-center">
            <span className="text-xs uppercase tracking-widest text-[#d4af37] font-mono">DuroTech28 · Low Voltage Specialist</span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight text-white mt-4 leading-[1.02]">
              Professional TV Mounting in Dallas–Fort Worth, TX
            </h1>
            <p className="text-sm sm:text-base text-zinc-300 max-w-2xl mx-auto leading-relaxed mt-6">
              DuroTech28 provides professional TV wall mounting, fireplace TV installation, wire concealment,
              and home entertainment setup throughout the Dallas–Fort Worth area. Whether you're installing a
              new television or upgrading your entertainment space, call us for a quote or book your installation online.
            </p>
            <div className="mt-8"><CTAPair idPrefix="dfw-hero" /></div>
            <p className="text-[11px] font-mono text-zinc-500 mt-4">Call <a href={PHONE_TEL} className="text-[#d4af37]">{PHONE_DISPLAY}</a> · Same-day appointments when available</p>
          </div>
        </header>

        <div className="max-w-5xl mx-auto px-6">
          {/* Pricing */}
          <section className="py-14" data-testid="dfw-pricing">
            <h2 className="font-display text-3xl uppercase text-white text-center">TV Mounting Prices</h2>
            <p className="text-xs text-zinc-500 font-mono text-center mt-2 mb-8">Transparent starting rates — confirmed before any work begins.</p>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="rounded-2xl overflow-hidden gold-border">
                <table className="w-full text-sm" data-testid="dfw-price-table">
                  <thead>
                    <tr className="bg-[#121216] text-[10px] font-mono uppercase tracking-widest text-zinc-400">
                      <th className="text-left p-4">TV Size</th>
                      <th className="p-4 text-right">Starting Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {PRICE_ROWS.map(([size, price]) => (
                      <tr key={size} className="border-t border-zinc-800/60">
                        <td className="p-4 text-white font-medium">{size}</td>
                        <td className="p-4 text-right font-mono text-[#d4af37]">{price}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="rounded-2xl overflow-hidden gold-border">
                <table className="w-full text-sm" data-testid="dfw-extras-table">
                  <thead>
                    <tr className="bg-[#121216] text-[10px] font-mono uppercase tracking-widest text-zinc-400">
                      <th className="text-left p-4">Additional Services</th>
                      <th className="p-4 text-right">Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {EXTRAS.map(([name, price]) => (
                      <tr key={name} className="border-t border-zinc-800/60">
                        <td className="p-4 text-white font-medium">{name}</td>
                        <td className="p-4 text-right font-mono text-[#d4af37]">{price}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <p className="text-[11px] text-zinc-500 font-mono mt-4 leading-relaxed" data-testid="dfw-pricing-disclaimer">
              All installation prices are starting rates. Specialty work, materials, electrical work and unusual
              mounting conditions may cost extra. Final pricing is always confirmed with you before work begins.
            </p>
          </section>

          {/* Payment options */}
          <section className="pb-14" data-testid="dfw-payment">
            <h2 className="font-display text-2xl uppercase text-white mb-6 text-center">Flexible Payment Options</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                ["Pay After Installation", "Payment is collected when your installation is completed. Installation-only bookings require no upfront payment — book your slot and pay when the job is done."],
                ["Pay Online", "Prefer to handle it now? Secure online payment is available through PayPal. It's always optional — you're never forced to check out online to book an appointment."],
                ["Deposit", "Mount packages may require a small upfront deposit to reserve your appointment. Deposits are credited toward your total — never an additional charge."],
              ].map(([t, d]) => (
                <div key={t} className="rounded-xl bg-[#121216] gold-border p-5">
                  <h3 className="text-sm font-semibold text-white">{t}</h3>
                  <p className="text-[11px] text-zinc-400 mt-2 leading-relaxed">{d}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Bundles */}
          <section className="pb-14" data-testid="dfw-bundles">
            <h2 className="font-display text-2xl uppercase text-white mb-2 text-center">Promotional Installation Bundles</h2>
            <p className="text-xs text-zinc-500 font-mono text-center mb-8">Currently available — book by phone or online.</p>
            <div className="grid md:grid-cols-2 gap-6">
              {BUNDLES.map((b) => (
                <div key={b.testid} className="rounded-2xl bg-[#121216] gold-border p-6 flex flex-col" data-testid={b.testid}>
                  <p className="font-display text-4xl text-[#d4af37]">${b.price}</p>
                  <h3 className="text-base font-semibold text-white mt-2">{b.title}</h3>
                  <ul className="mt-4 space-y-2 flex-1">
                    {b.items.map((i) => (
                      <li key={i} className="text-[11px] text-zinc-400 leading-relaxed flex gap-2">
                        <span className="text-[#d4af37] mt-0.5">•</span>{i}
                      </li>
                    ))}
                  </ul>
                  <div className="flex flex-wrap gap-3 mt-6">
                    <a href={PHONE_TEL} data-testid={`${b.testid}-call-btn`}
                      className="flex-1 text-center px-4 py-3 rounded border-2 border-[#d4af37] text-[#d4af37] font-bold uppercase tracking-wider text-[11px] hover:bg-[#d4af37] hover:text-black transition-colors">
                      Call Now
                    </a>
                    <Link to="/book" data-testid={`${b.testid}-book-btn`}
                      className="flex-1 text-center px-4 py-3 rounded bg-gradient-to-r from-[#d4af37] to-[#e6ca65] text-black font-bold uppercase tracking-wider text-[11px] hover:opacity-95 transition-opacity">
                      Book Online
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Services */}
          <section className="pb-14" data-testid="dfw-services">
            <h2 className="font-display text-2xl uppercase text-white mb-8 text-center">TV Installation Services</h2>
            <div className="grid sm:grid-cols-2 gap-5">
              {SERVICES.map(([t, d], i) => (
                <div key={t} className="rounded-xl bg-[#121216] border border-zinc-800 p-5">
                  <h3 className="text-sm font-semibold text-white">{t}</h3>
                  <p className="text-[11px] text-zinc-400 mt-2 leading-relaxed">{d}</p>
                  <div className="flex gap-4 mt-3">
                    <Link to="/book" data-testid={`dfw-service-book-${i}`} className="text-[11px] font-mono uppercase tracking-wider text-[#d4af37] hover:underline">Book Online →</Link>
                    <a href={PHONE_TEL} data-testid={`dfw-service-call-${i}`} className="text-[11px] font-mono uppercase tracking-wider text-zinc-400 hover:text-[#d4af37]">Call {PHONE_DISPLAY}</a>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Service areas */}
          <section className="pb-14" data-testid="dfw-areas">
            <h2 className="font-display text-2xl uppercase text-white mb-4 text-center">Serving the Dallas–Fort Worth Metroplex</h2>
            <p className="text-sm text-zinc-400 max-w-2xl mx-auto text-center leading-relaxed mb-6">
              DuroTech28 is a mobile installation service — we come to you anywhere in the DFW area.
              Whether it's a bedroom TV in Irving, a fireplace mount in Frisco, or a full entertainment
              setup in Plano, the same professional standard applies at every address.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {CITIES.map((c) => (
                <div key={c} data-testid={`dfw-city-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  className="rounded-xl bg-[#121216] border border-zinc-800 p-4">
                  <h3 className="text-sm font-semibold text-white">TV Mounting in {c}, TX</h3>
                  <p className="text-[11px] text-zinc-400 mt-1.5 leading-relaxed">{CITY_NOTES[c]}</p>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="pb-16" data-testid="dfw-faq">
            <h2 className="font-display text-2xl uppercase text-white mb-8 text-center">TV Mounting FAQs</h2>
            <div className="space-y-3 max-w-3xl mx-auto">
              {FAQS.map(([q, a], i) => (
                <details key={q} className="rounded-xl bg-[#121216] border border-zinc-800 px-5 py-4 group" data-testid={`dfw-faq-${i}`}>
                  <summary className="text-sm font-semibold text-white cursor-pointer list-none flex justify-between items-center gap-3">
                    {q}
                    <span className="text-[#d4af37] font-mono text-lg group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <p className="text-[12px] text-zinc-400 mt-3 leading-relaxed">{a}</p>
                </details>
              ))}
            </div>
            <p className="text-center text-[11px] font-mono text-zinc-500 mt-8">
              Looking for everything we do? Visit our <Link to="/" className="text-[#d4af37] hover:underline">homepage</Link> or
              browse the full <Link to="/services" className="text-[#d4af37] hover:underline">services catalog</Link>.
            </p>
            <div className="mt-8"><CTAPair idPrefix="dfw-bottom" /></div>
          </section>
        </div>
      </main>

      {/* Sticky mobile call button */}
      <a href={PHONE_TEL} data-testid="dfw-sticky-call"
        className="sm:hidden fixed bottom-4 inset-x-4 z-40 text-center py-3.5 rounded-full bg-gradient-to-r from-[#d4af37] to-[#e6ca65] text-black font-bold uppercase tracking-wider text-xs shadow-lg shadow-black/50">
        Call Now — {PHONE_DISPLAY}
      </a>

      <Footer />
    </>
  );
}
